// ABOUTME: Favorite star in the configurator header, toggling the configured prototype's bookmark.
// ABOUTME: Targets the PROTOTYPE hierarchy, like the catalog card star; hidden when favorites are off or favoriteRoles forbid it.

import { Box, IconButton, StarBorderIcon, StarIcon, Tooltip } from '@ds/coolshop';
import {
    useCanAddFavorite,
    useFavoritesEnabled,
    useFavoriteToggle,
} from '@src/_new/modules/user-favorites';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext } from 'react';
import { useIntl } from 'react-intl';

export function ConfiguratorFavoriteAction({ prototypeId }: { prototypeId: string }) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const canAddFavorite = useCanAddFavorite();
    const favoritesEnabled = useFavoritesEnabled();
    const hierarchyId = settings.prototype?.hierarchies?.prototype ?? '';

    const { isFavorite, toggling, toggleWithFeedback } = useFavoriteToggle({
        hierarchyId,
        nodeId: prototypeId,
        skip: !favoritesEnabled || !hierarchyId || !prototypeId,
    });

    if (!favoritesEnabled || !hierarchyId || !prototypeId) return null;
    // Removing is always allowed: a favorite already set keeps its star.
    if (!isFavorite && !canAddFavorite(hierarchyId)) return null;

    const label = isFavorite
        ? formatMessage({ id: 'catalog.favorite.remove', defaultMessage: 'Remove from favorites' })
        : formatMessage({ id: 'catalog.favorite.add', defaultMessage: 'Add to favorites' });

    // Styled like the other actions of the configurator's primary-coloured ActionHeader (Save as
    // Template): a round contained icon on the DS inverted palette, its label in a tooltip.
    return (
        <Tooltip title={label} placement="bottom">
            {/* A wrapper, so the tooltip still listens while the button is disabled. */}
            <Box display="inline-flex">
                <IconButton
                    variant="contained"
                    size="medium"
                    invertedColor
                    contrastColor="primary"
                    aria-label={label}
                    data-testid={`configurator-favorite-button-${prototypeId}`}
                    disabled={toggling}
                    onClick={() => {
                        toggleWithFeedback();
                    }}
                >
                    {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
            </Box>
        </Tooltip>
    );
}
